import React, { useState, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Trash2,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextField, InputAdornment, Autocomplete, LinearProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { DateRangePicker, AssignedToFilter } from '@molecules';
import { ChevronStatusFilter } from '@molecules/ChevronStatusFilter';
import { trpc } from '@lib/trpc';
import { InvoiceStatus, UserWithRoles, InvoiceRecord, StoreRecord } from '@types';
import { MANDATORY_ASSIGNMENT_STATUSES } from '@config/workflow-config';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useAuth } from '@contexts/AuthContext';
import {
  buildReturnUrl,
  getEditUrlWithReturn,
  validateUrlStatus,
  getDefaultStatusForUser,
  StatusFilterType,
} from '@utils/navigationHelpers';
import { useDebounce } from '@hooks/usePerformance';
import { saveAssignedTo, getAssignedTo } from '@utils/assignedToStorage';
import { muiTheme } from '@/theme/muiTheme';

// Sorting types
type SortField = 'supplier' | 'invoice_date' | 'due_date';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField | null;
  direction: SortDirection;
}

// Pagination constants
const ITEMS_PER_PAGE = 10;

const formatDateForAPI = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

const parseDateParam = (value: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

interface InvoiceListProps {
  initialStatus?: InvoiceStatus | null;
}

export const InvoiceList: React.FC<InvoiceListProps> = ({ initialStatus }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') ?? '');
  const [supplierFilter, setSupplierFilter] = useState(() => searchParams.get('supplier') ?? '');
  const [selectedStore, setSelectedStore] = useState<StoreRecord | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => [
    parseDateParam(searchParams.get('dateStart')),
    parseDateParam(searchParams.get('dateEnd')),
  ]);

  // Initialize assignedToFilter from URL, localStorage, or default to 'all'
  const [assignedToFilter, setAssignedToFilter] = useState<string>(() => {
    const urlValue = searchParams.get('assignedTo');
    if (urlValue) return urlValue;

    const storedValue = getAssignedTo();
    if (storedValue) return storedValue;

    return 'all';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageInput, setCustomPageInput] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: null, direction: 'asc' });
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [lastSelectedInvoiceId, setLastSelectedInvoiceId] = useState<string | null>(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState<UserWithRoles | null>(
    null
  );

  // Initialize selectedStatus from URL or props
  const urlStatus = searchParams.get('status') as StatusFilterType | null;
  const defaultStatus = getDefaultStatusForUser(user?.roles);
  const validatedStatus = validateUrlStatus(urlStatus || initialStatus, user?.roles, defaultStatus);
  const [selectedStatus, setSelectedStatus] = useState<StatusFilterType>(validatedStatus);

  // Check if user has admin or finance permissions for advanced assignment features
  const hasAdminOrFinance =
    user?.roles?.some(role => role.name === 'admin' || role.name === 'finance') ?? false;

  // Check if user is admin (for delete permission)
  const isAdmin = user?.roles?.some(role => role.name === 'admin') ?? false;

  // Check if user can use bulk assignment (includes stock role)
  const canUseBulkAssignment =
    user?.roles?.some(
      role => role.name === 'admin' || role.name === 'finance' || role.name === 'stock'
    ) ?? false;

  // Status change handler that updates URL
  const handleStatusChange = (status: StatusFilterType) => {
    setSelectedStatus(status);
    if (status === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', status);
    }

    // Manage assignedTo URL param: remove on non-assignment statuses, restore on assignment statuses
    const isAssignmentStatusNext =
      status !== 'all' && MANDATORY_ASSIGNMENT_STATUSES.includes(status as InvoiceStatus);
    if (isAssignmentStatusNext) {
      // Restore from localStorage if filter is 'all'
      const currentFilter =
        assignedToFilter === 'all' ? getAssignedTo() || 'all' : assignedToFilter;

      if (currentFilter === 'all') {
        searchParams.delete('assignedTo');
      } else {
        searchParams.set('assignedTo', currentFilter);
        // Update state if restored from localStorage
        if (currentFilter !== assignedToFilter) {
          setAssignedToFilter(currentFilter);
        }
      }
    } else {
      // Remove from URL but keep in state and localStorage so it can be restored later
      searchParams.delete('assignedTo');
    }

    setSearchParams(searchParams);
  };

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);

    const nextSearchParams = new URLSearchParams(searchParams);
    const trimmedValue = value.trim();
    if (trimmedValue) {
      nextSearchParams.set('search', trimmedValue);
    } else {
      nextSearchParams.delete('search');
    }
    nextSearchParams.delete('page');
    setSearchParams(nextSearchParams);
  };

  const handleSupplierFilterChange = (value: string) => {
    setSupplierFilter(value);
    setCurrentPage(1);

    const nextSearchParams = new URLSearchParams(searchParams);
    const trimmedValue = value.trim();
    if (trimmedValue) {
      nextSearchParams.set('supplier', trimmedValue);
    } else {
      nextSearchParams.delete('supplier');
    }
    nextSearchParams.delete('page');
    setSearchParams(nextSearchParams);
  };

  const handleStoreFilterChange = (store: StoreRecord | null) => {
    setSelectedStore(store);
    setCurrentPage(1);

    const nextSearchParams = new URLSearchParams(searchParams);
    if (store) {
      nextSearchParams.set('storeId', store.id);
    } else {
      nextSearchParams.delete('storeId');
    }
    nextSearchParams.delete('page');
    setSearchParams(nextSearchParams);
  };

  const handleDateRangeChange = (range: [Date | null, Date | null]) => {
    setDateRange(range);
    setCurrentPage(1);

    const nextSearchParams = new URLSearchParams(searchParams);
    if (range[0] && range[1]) {
      nextSearchParams.set('dateStart', formatDateForAPI(range[0]));
      nextSearchParams.set('dateEnd', formatDateForAPI(range[1]));
    } else {
      nextSearchParams.delete('dateStart');
      nextSearchParams.delete('dateEnd');
    }
    nextSearchParams.delete('page');
    setSearchParams(nextSearchParams);
  };

  // Auto-correct selectedStatus when user roles change or on initial load
  React.useEffect(() => {
    if (user && user.roles) {
      const appropriateStatus = getDefaultStatusForUser(user.roles);
      const validatedStatus = validateUrlStatus(selectedStatus, user.roles, appropriateStatus);

      if (validatedStatus !== selectedStatus) {
        handleStatusChange(validatedStatus);
      }
    }
  }, [user?.roles]);

  // Clear selected user assignment for stock-only users (they should only use "Assign to Me")
  React.useEffect(() => {
    if (user && user.roles && !hasAdminOrFinance && selectedUserForAssignment) {
      setSelectedUserForAssignment(null);
    }
  }, [user?.roles, hasAdminOrFinance, selectedUserForAssignment]);

  // Debounce filter values to prevent excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const debouncedSupplierFilter = useDebounce(supplierFilter, 300);

  // Check if current status requires assignment filtering
  const isAssignmentStatus =
    selectedStatus !== 'all' &&
    MANDATORY_ASSIGNMENT_STATUSES.includes(selectedStatus as InvoiceStatus);

  // Build query input from current filter state
  const queryInput = useMemo(
    () => ({
      status: selectedStatus === 'all' ? 'unprocessed' : selectedStatus,
      ...(debouncedSearchTerm ? { invoiceNumber: debouncedSearchTerm } : {}),
      ...(debouncedSupplierFilter ? { supplierName: debouncedSupplierFilter } : {}),
      ...(selectedStore ? { storeId: selectedStore.id } : {}),
      ...(dateRange[0] && dateRange[1]
        ? {
            invoiceDateRange: {
              startDate: formatDateForAPI(dateRange[0]),
              endDate: formatDateForAPI(dateRange[1]),
            },
          }
        : {}),
      ...(isAssignmentStatus && assignedToFilter !== 'all' ? { assignedTo: assignedToFilter } : {}),
      ...(selectedStatus === 'completed'
        ? { pagination: { limit: ITEMS_PER_PAGE, offset: (currentPage - 1) * ITEMS_PER_PAGE } }
        : {}),
    }),
    [
      selectedStatus,
      debouncedSearchTerm,
      debouncedSupplierFilter,
      selectedStore,
      dateRange,
      assignedToFilter,
      isAssignmentStatus,
      currentPage,
    ]
  );

  // tRPC utilities for cache management
  const utils = trpc.useUtils();

  // tRPC queries and mutations
  const {
    data: invoiceData,
    isLoading,
    isFetching,
  } = trpc.invoice.getInvoices.useQuery(queryInput, {
    enabled: selectedStatus !== 'all', // Don't fetch when 'all' is selected
    refetchInterval: false,
    placeholderData: keepPreviousData, // React Query v5: keep previous data during transitions
  });

  // Extract invoice data with defaults
  const invoices = invoiceData?.invoices ?? [];
  const totalCount = invoiceData?.totalCount ?? 0;

  // Build counts query input (same filters but without status and pagination)
  const countsInput = useMemo(
    () => ({
      ...(debouncedSearchTerm ? { invoiceNumber: debouncedSearchTerm } : {}),
      ...(debouncedSupplierFilter ? { supplierName: debouncedSupplierFilter } : {}),
      ...(selectedStore ? { storeId: selectedStore.id } : {}),
      ...(dateRange[0] && dateRange[1]
        ? {
            invoiceDateRange: {
              startDate: formatDateForAPI(dateRange[0]),
              endDate: formatDateForAPI(dateRange[1]),
            },
          }
        : {}),
      ...(assignedToFilter !== 'all' ? { assignedTo: assignedToFilter } : {}),
    }),
    [debouncedSearchTerm, debouncedSupplierFilter, selectedStore, dateRange, assignedToFilter]
  );

  // Query for status counts (for ChevronStatusFilter)
  const { data: statusCounts = {} } = trpc.invoice.getInvoiceCounts.useQuery(countsInput, {
    refetchInterval: false,
  });

  // Fetch users for assignment dropdown - only for admin/finance users
  const { data: users = [] } = trpc.user.getAllActiveUsersWithRoles.useQuery(undefined, {
    enabled: !!(user && hasAdminOrFinance), // Only fetch if user exists and has admin or finance role
    retry: false, // Don't retry on failure to prevent multiple error toasts
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    refetchOnMount: false, // Don't refetch on mount
  });

  const { data: storesData, isLoading: isLoadingStores } = trpc.store.getAll.useQuery();
  const stores = storesData?.data ?? [];

  React.useEffect(() => {
    const storeIdFromUrl = searchParams.get('storeId');
    if (!storeIdFromUrl) {
      return;
    }

    const matchingStore = stores.find(store => store.id === storeIdFromUrl);
    if (matchingStore && selectedStore?.id !== matchingStore.id) {
      setSelectedStore(matchingStore);
    }
  }, [searchParams, selectedStore?.id, stores]);

  // Handle assignment filter change
  const handleAssignedToFilterChange = (value: string) => {
    setAssignedToFilter(value);

    // Persist to localStorage
    saveAssignedTo(value);

    // Update URL params
    if (value === 'all') {
      searchParams.delete('assignedTo');
    } else {
      searchParams.set('assignedTo', value);
    }
    setSearchParams(searchParams);
  };

  // Bulk assignment mutations
  const bulkAssignMutation = trpc.invoice.bulkAssignToMe.useMutation({
    onSuccess: data => {
      toast.success(`Successfully assigned ${data.assignedCount} invoices to you`);
      setSelectedInvoices(new Set());
      utils.invoice.getInvoices.invalidate();
      utils.invoice.getInvoiceCounts.invalidate();
    },
    onError: error => {
      toast.error(error.message || 'Failed to assign invoices');
    },
    onSettled: () => {
      setBulkActionLoading(false);
    },
  });

  const bulkAssignToUserMutation = trpc.invoice.bulkAssignToUser.useMutation({
    onSuccess: data => {
      toast.success(`Successfully assigned ${data.assignedCount} invoices`);
      setSelectedInvoices(new Set());
      utils.invoice.getInvoices.invalidate();
      utils.invoice.getInvoiceCounts.invalidate();
    },
    onError: error => {
      toast.error(error.message || 'Failed to assign invoices');
    },
    onSettled: () => {
      setBulkActionLoading(false);
    },
  });

  const bulkDeleteMutation = trpc.invoice.bulkDelete.useMutation({
    onSuccess: data => {
      toast.success(
        `Successfully deleted ${data.deletedCount} invoice${data.deletedCount === 1 ? '' : 's'}`
      );
      setSelectedInvoices(new Set());
      utils.invoice.getInvoices.invalidate();
      utils.invoice.getInvoiceCounts.invalidate();
    },
    onError: error => {
      toast.error(error.message || 'Failed to delete invoices');
    },
    onSettled: () => {
      setBulkActionLoading(false);
    },
  });

  // Note: Bulk unassignment removed - unassignment is handled manually per invoice
  // to prevent accidental bulk unassignments

  // Apply client-side sorting to server-fetched invoices
  const sortedInvoices = useMemo(() => {
    if (!sortConfig.field) {
      return invoices;
    }

    return [...invoices].sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;

      switch (sortConfig.field) {
        case 'supplier':
          aValue = a.supplier.toLowerCase();
          bValue = b.supplier.toLowerCase();
          break;
        case 'invoice_date':
          aValue = new Date(a.invoice_date);
          bValue = new Date(b.invoice_date);
          break;
        case 'due_date':
          // Handle null due dates by treating them as far future dates for sorting
          aValue = a.due_date ? new Date(a.due_date) : new Date('9999-12-31');
          bValue = b.due_date ? new Date(b.due_date) : new Date('9999-12-31');
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [invoices, sortConfig]);

  // Server-side pagination - use sorted invoices directly (no client-side slicing needed for data)
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const paginatedInvoices = sortedInvoices;

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, searchTerm, supplierFilter, selectedStore, dateRange, assignedToFilter]);

  const handleRowClick = (invoice: InvoiceRecord) => {
    const returnUrl = buildReturnUrl(searchParams);
    const editUrl = getEditUrlWithReturn(invoice.id, returnUrl);
    navigate(editUrl);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCustomPageSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const requestedPage = Number.parseInt(customPageInput, 10);
    if (!Number.isFinite(requestedPage)) {
      return;
    }

    const clampedPage = Math.min(Math.max(requestedPage, 1), totalPages);
    setCurrentPage(clampedPage);
    setCustomPageInput(String(clampedPage));
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };

  // Sorting logic
  const handleSort = (field: SortField) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-4 h-4 text-primary-600" />
    ) : (
      <ChevronDown className="w-4 h-4 text-primary-600" />
    );
  };

  // Create a memoized currency formatter instance
  const currencyFormatter = React.useMemo(() => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const formatCurrency = React.useCallback(
    (amount: string | number): string => {
      try {
        // Handle empty/null/Unknown values
        if (
          !amount ||
          amount === 'Unknown' ||
          (typeof amount === 'string' && amount.trim() === '')
        ) {
          return '';
        }

        // Handle string or number input
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;

        // Validate the number
        if (!Number.isFinite(num)) {
          return '';
        }

        return currencyFormatter.format(num).replace('A$', '$');
      } catch (error) {
        // Error formatting currency - return empty string
        return '';
      }
    },
    [currencyFormatter]
  );

  const formatDate = (dateString: string) => {
    if (!dateString || dateString.trim() === '' || dateString === 'Unknown') {
      return '';
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '';
      }
      return format(date, 'dd/MM/yyyy');
    } catch (error) {
      return '';
    }
  };

  // Multi-select handlers
  const handleSelectInvoice = (invoiceId: string, checked: boolean, shiftKey = false) => {
    const selectableInvoiceIds = paginatedInvoices
      .filter(invoice => !invoice.assigned_to_user)
      .map(invoice => invoice.id);
    const clickedIndex = selectableInvoiceIds.indexOf(invoiceId);
    const anchorIndex = lastSelectedInvoiceId
      ? selectableInvoiceIds.indexOf(lastSelectedInvoiceId)
      : -1;

    const newSelection = new Set(selectedInvoices);

    if (shiftKey && anchorIndex !== -1 && clickedIndex !== -1) {
      const rangeStart = Math.min(anchorIndex, clickedIndex);
      const rangeEnd = Math.max(anchorIndex, clickedIndex);
      const rangeIds = selectableInvoiceIds.slice(rangeStart, rangeEnd + 1);

      rangeIds.forEach(rangeInvoiceId => {
        if (checked) {
          newSelection.add(rangeInvoiceId);
        } else {
          newSelection.delete(rangeInvoiceId);
        }
      });
    } else if (checked) {
      newSelection.add(invoiceId);
    } else {
      newSelection.delete(invoiceId);
    }

    setSelectedInvoices(newSelection);
    setLastSelectedInvoiceId(invoiceId);
  };

  const handleSelectAllVisible = (checked: boolean) => {
    if (checked) {
      // Only select unassigned invoices
      const unassignedVisibleIds = paginatedInvoices
        .filter(invoice => !invoice.assigned_to_user)
        .map(invoice => invoice.id);
      setSelectedInvoices(new Set([...selectedInvoices, ...unassignedVisibleIds]));
    } else {
      const visibleIds = new Set(paginatedInvoices.map(invoice => invoice.id));
      const newSelection = new Set([...selectedInvoices].filter(id => !visibleIds.has(id)));
      setSelectedInvoices(newSelection);
    }
  };

  const clearSelection = () => {
    setSelectedInvoices(new Set());
    setLastSelectedInvoiceId(null);
    setSelectedUserForAssignment(null); // Also clear selected user
  };

  // Bulk action handlers
  const handleBulkAssignToMe = async () => {
    if (selectedInvoices.size === 0) {
      toast.error('Please select invoices to assign');
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to assign ${selectedInvoices.size} selected invoice${selectedInvoices.size === 1 ? '' : 's'} to yourself?`
      )
    ) {
      return;
    }

    setBulkActionLoading(true);
    const invoiceIds = Array.from(selectedInvoices);
    bulkAssignMutation.mutate({ invoiceIds });
  };

  // Handle user selection from autocomplete
  const handleUserSelection = (user: UserWithRoles | null) => {
    setSelectedUserForAssignment(user);
  };

  // Handle confirmed assignment to selected user
  const handleConfirmAssignToSelectedUser = async () => {
    if (selectedInvoices.size === 0) {
      toast.error('Please select invoices to assign');
      return;
    }

    if (!selectedUserForAssignment) {
      toast.error('Please select a user to assign to');
      return;
    }

    const userName = selectedUserForAssignment.name || selectedUserForAssignment.email;
    if (
      !window.confirm(
        `Are you sure you want to assign ${selectedInvoices.size} selected invoice${selectedInvoices.size === 1 ? '' : 's'} to ${userName}?`
      )
    ) {
      return;
    }

    setBulkActionLoading(true);
    const invoiceIds = Array.from(selectedInvoices);

    try {
      await bulkAssignToUserMutation.mutateAsync({
        invoiceIds,
        userId: selectedUserForAssignment.id,
      });
      setSelectedUserForAssignment(null); // Clear selection after successful assignment
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  // Handle bulk delete (admin only)
  const handleBulkDelete = async () => {
    if (selectedInvoices.size === 0) {
      toast.error('Please select invoices to delete');
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to permanently delete ${selectedInvoices.size} selected invoice${selectedInvoices.size === 1 ? '' : 's'}? This action cannot be undone.`
      )
    ) {
      return;
    }

    setBulkActionLoading(true);
    const invoiceIds = Array.from(selectedInvoices);
    bulkDeleteMutation.mutate({ invoiceIds });
  };

  // Check if all visible unassigned invoices are selected
  const unassignedVisibleInvoices = paginatedInvoices.filter(invoice => !invoice.assigned_to_user);
  const allVisibleSelected =
    unassignedVisibleInvoices.length > 0 &&
    unassignedVisibleInvoices.every(invoice => selectedInvoices.has(invoice.id));
  const someVisibleSelected = paginatedInvoices.some(invoice => selectedInvoices.has(invoice.id));

  // Only show full-page loading for true initial load (never had any data)
  // Use isFetching with keepPreviousData to show previous data during transitions
  const showFullPageLoading = isLoading && invoices.length === 0 && !isFetching;

  return (
    <div className="space-y-6">
      {/* Enhanced Search and Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-4">
          <ThemeProvider theme={muiTheme}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <TextField
                label="Invoice Number"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={e => handleSearchTermChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="w-5 h-5 text-gray-400" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: '100%' }}
              />

              {/* Supplier Filter */}
              <TextField
                label="Supplier"
                variant="outlined"
                size="small"
                value={supplierFilter}
                onChange={e => handleSupplierFilterChange(e.target.value)}
                sx={{ width: '100%' }}
              />

              {/* Store Filter */}
              <Autocomplete
                options={stores}
                getOptionLabel={store => store.name}
                value={selectedStore}
                onChange={(_, store) => handleStoreFilterChange(store)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={isLoadingStores}
                renderInput={params => (
                  <TextField {...params} label="Store" variant="outlined" size="small" />
                )}
                noOptionsText="No stores found"
                clearOnEscape
              />

              {/* Date Range Picker */}
              <DateRangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                placeholder="Select date range"
              />
            </div>
          </ThemeProvider>
        </div>
      </div>

      {/* Status Filter - Using New Dynamic Chevron Component */}
      <ChevronStatusFilter
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        title="Invoice Status Overview"
        showAll={false}
        filteredCounts={statusCounts}
        extraHeaderRight={
          isAssignmentStatus ? (
            <div className="w-64">
              <ThemeProvider theme={muiTheme}>
                <AssignedToFilter
                  value={assignedToFilter}
                  onChange={handleAssignedToFilterChange}
                  users={hasAdminOrFinance ? users : []}
                  disabled={false}
                  size="small"
                />
              </ThemeProvider>
            </div>
          ) : undefined
        }
      />

      {/* Bulk Actions - Available to admin/finance/stock users */}
      {selectedInvoices.size > 0 && selectedStatus !== 'unprocessed' && canUseBulkAssignment && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-secondary-900">
                {selectedInvoices.size} invoice{selectedInvoices.size === 1 ? '' : 's'} selected
              </span>
              <button
                onClick={clearSelection}
                className="text-sm text-secondary-500 hover:text-secondary-700 underline"
              >
                Clear selection
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
              {/* User Selection Autocomplete - Only for admin/finance users */}
              {hasAdminOrFinance && (
                <div className="flex items-center space-x-2">
                  <ThemeProvider theme={muiTheme}>
                    <Autocomplete
                      options={users}
                      getOptionLabel={user => user.name || user.email}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Select user to assign"
                          variant="outlined"
                          size="small"
                          sx={{ minWidth: '200px' }}
                        />
                      )}
                      onChange={(_, selectedUser) => {
                        handleUserSelection(selectedUser);
                      }}
                      disabled={bulkActionLoading || users.length === 0}
                      value={selectedUserForAssignment}
                      renderOption={(props, user) => (
                        <li {...props} key={user.id}>
                          <div>
                            <div className="font-medium">{user.name || user.email}</div>
                            {user.name && <div className="text-xs text-gray-500">{user.email}</div>}
                            {user.roles && user.roles.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {user.roles.map(
                                  (role: { id: string; displayName: string; color?: string }) => (
                                    <span
                                      key={role.id}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                      style={{
                                        backgroundColor: role.color ? `${role.color}20` : '#e5e7eb',
                                        color: role.color || '#6b7280',
                                        border: `1px solid ${role.color || '#d1d5db'}`,
                                      }}
                                    >
                                      {role.displayName}
                                    </span>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </li>
                      )}
                      noOptionsText="No users available"
                      clearOnEscape
                      blurOnSelect
                    />
                  </ThemeProvider>
                </div>
              )}

              {/* Quick Actions - Stock users only see "Assign to Me", admin/finance can assign to others */}
              {hasAdminOrFinance && selectedUserForAssignment ? (
                <button
                  onClick={handleConfirmAssignToSelectedUser}
                  disabled={bulkActionLoading}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-sm font-medium transition-colors duration-150 flex items-center space-x-2"
                >
                  {bulkActionLoading && bulkAssignToUserMutation.isPending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : null}
                  <span>
                    Assign To {selectedUserForAssignment.name || selectedUserForAssignment.email}
                  </span>
                </button>
              ) : (
                <button
                  onClick={handleBulkAssignToMe}
                  disabled={bulkActionLoading}
                  className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-sm font-medium transition-colors duration-150 flex items-center space-x-2"
                >
                  {bulkActionLoading && bulkAssignMutation.isPending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : null}
                  <span>Assign to Me</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Button - Admin only */}
      {isAdmin && selectedInvoices.size > 0 && (
        <button
          onClick={handleBulkDelete}
          disabled={bulkActionLoading}
          className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-sm font-medium transition-colors duration-150 flex items-center space-x-2"
        >
          {bulkActionLoading && bulkDeleteMutation.isPending ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
          <span>Delete</span>
        </button>
      )}

      {/* Invoice List */}
      <div className="bg-white rounded-lg shadow overflow-hidden relative">
        {/* Loading indicator - subtle linear progress */}
        {isFetching && (
          <div className="absolute top-0 left-0 right-0 z-10">
            <ThemeProvider theme={muiTheme}>
              <LinearProgress color="primary" />
            </ThemeProvider>
          </div>
        )}
        {showFullPageLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="w-8 h-8 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : invoices.length === 0 && !isFetching ? (
          <div className="text-center py-8 text-secondary-500">
            {debouncedSearchTerm ? 'No invoices found matching your search.' : 'No invoices found.'}
          </div>
        ) : invoices.length === 0 ? (
          // Show minimal loading placeholder while fetching new page data
          <div className="flex justify-center items-center py-8">
            <div className="w-6 h-6 border-3 border-accent-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  {/* Selection checkbox column - available to admin/finance/stock users */}
                  {canUseBulkAssignment && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        ref={el => {
                          if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                        }}
                        onChange={e => handleSelectAllVisible(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </th>
                  )}
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100 transition-colors duration-150"
                    onClick={() => handleSort('supplier')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Supplier</span>
                      {getSortIcon('supplier')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Invoice Number
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100 transition-colors duration-150"
                    onClick={() => handleSort('invoice_date')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Invoice Date</span>
                      {getSortIcon('invoice_date')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100 transition-colors duration-150"
                    onClick={() => handleSort('due_date')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Due Date</span>
                      {getSortIcon('due_date')}
                    </div>
                  </th>
                  {/* Temporarily hiding status column */}
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Status
                  </th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Total
                  </th>
                  {selectedStatus !== 'unprocessed' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Assigned To
                    </th>
                  )}
                  <th className="px-6 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {paginatedInvoices.map(invoice => (
                  <tr
                    key={invoice.id}
                    className={`hover:bg-secondary-50 transition-colors duration-150 ${
                      selectedInvoices.has(invoice.id) ? 'bg-primary-50' : ''
                    }`}
                  >
                    {/* Selection checkbox - available to admin/finance/stock users */}
                    {canUseBulkAssignment && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.has(invoice.id)}
                          disabled={!!invoice.assigned_to_user}
                          onChange={e => {
                            e.stopPropagation();
                            handleSelectInvoice(
                              invoice.id,
                              e.target.checked,
                              (e.nativeEvent as MouseEvent).shiftKey
                            );
                          }}
                          className={`h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded ${
                            invoice.assigned_to_user ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        />
                      </td>
                    )}
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleRowClick(invoice)}
                    >
                      <div className="text-sm font-medium text-secondary-900">
                        {invoice.supplier && invoice.supplier !== 'Unknown' ? invoice.supplier : ''}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleRowClick(invoice)}
                    >
                      <div className="text-sm text-secondary-900">
                        {invoice.invoice_number && invoice.invoice_number !== 'Unknown'
                          ? invoice.invoice_number
                          : ''}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleRowClick(invoice)}
                    >
                      <div className="text-sm text-secondary-900">
                        {formatDate(invoice.invoice_date)}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleRowClick(invoice)}
                    >
                      <div className="text-sm text-secondary-900">
                        {formatDate(invoice.due_date || '')}
                      </div>
                    </td>
                    {/* Temporarily hiding status column */}
                    {/* <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invoice.status)}
                    </td> */}
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleRowClick(invoice)}
                    >
                      <div className="text-sm font-semibold text-secondary-900">
                        {formatCurrency(invoice.total_amount)}
                      </div>
                    </td>
                    {selectedStatus !== 'unprocessed' && (
                      <td
                        className="px-6 py-4 whitespace-nowrap cursor-pointer"
                        onClick={() => handleRowClick(invoice)}
                      >
                        <div className="text-sm text-secondary-900">
                          {invoice.assigned_to_user ? (
                            <div>
                              <div className="font-medium">
                                {invoice.assigned_to_user.name || invoice.assigned_to_user.email}
                              </div>
                              {invoice.assigned_to_user.name && (
                                <div className="text-xs text-secondary-500">
                                  {invoice.assigned_to_user.email}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span></span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleRowClick(invoice);
                        }}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors duration-150"
                      >
                        Process
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination - Show only for Completed status (stage 12) when there are multiple pages */}
        {selectedStatus === 'completed' && totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)}
                  </span>{' '}
                  of <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <form
                  onSubmit={handleCustomPageSubmit}
                  className="flex items-center space-x-2"
                  noValidate
                >
                  <label htmlFor="completed-page-jump" className="text-sm text-gray-700">
                    Page
                  </label>
                  <input
                    id="completed-page-jump"
                    type="number"
                    min={1}
                    max={totalPages}
                    value={customPageInput}
                    onChange={event => setCustomPageInput(event.target.value)}
                    placeholder={String(currentPage)}
                    aria-label="Page number"
                    className="w-20 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <button
                    type="submit"
                    className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    Go
                  </button>
                </form>

                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNumber;
                    if (totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + i;
                    } else {
                      pageNumber = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`relative inline-flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                          currentPage === pageNumber
                            ? 'bg-primary-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>

                <button
                  onClick={handleLastPage}
                  disabled={currentPage === totalPages}
                  aria-label="Last Page"
                  className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last Page
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
